// Calendar-date arithmetic — the primitives every date in this system is built
// on, shared by the backend (`src/core/date/calendar.ts`) and the frontend
// (`frontend/src/lib/calendar.ts`), both of which re-export from here. No React,
// no I/O, no platform globals beyond `Date`.
//
// A *calendar date* is a `yyyy-mm-dd` string and has no time zone. That is the
// whole reason this file exists rather than each caller reaching for `Date`:
// `new Date('2026-03-15')` parses as UTC midnight but every getter reads it in
// the runtime's zone, so west of Greenwich the date you get back is the 14th.
// Everything here parses to `Date.UTC` and formats out of the UTC getters, so a
// calendar date that goes in comes back out unshifted, on any machine.
//
// These lived in `@synapse/gestation` until the analytics feature needed them.
// The functions were always generic — `addDays` knows nothing about pregnancy —
// but the package name meant an inventory report listed pregnancy-dating math as
// a dependency, and someone looking for "where does calendar arithmetic live"
// had two plausible answers. Now there is one, and `@synapse/gestation` is a
// consumer of it like everything else.

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

/** Parse a `yyyy-mm-dd` string to UTC epoch-ms at midnight. Throws on a malformed
 *  or non-existent date (e.g. 2024-02-30). */
function parseIsoUtc(iso: string): number {
  const m = ISO_DATE_RE.exec(iso);
  if (!m) throw new RangeError(`Invalid ISO date: ${JSON.stringify(iso)}`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const ms = Date.UTC(year, month - 1, day);
  const d = new Date(ms);
  // Reject values that JS silently rolled over (e.g. month 13, day 30 in Feb).
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    throw new RangeError(`Invalid calendar date: ${iso}`);
  }
  return ms;
}

function formatIsoUtc(ms: number): string {
  const d = new Date(ms);
  const y = String(d.getUTCFullYear()).padStart(4, "0");
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const da = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** True when `s` is a well-formed, existing `yyyy-mm-dd` calendar date. Use this to
 *  validate free-form dates (e.g. an LMP from consultation JSON) BEFORE the throwing
 *  helpers below. */
export function isValidIsoDate(s: unknown): s is string {
  if (typeof s !== "string") return false;
  try {
    parseIsoUtc(s);
    return true;
  } catch {
    return false;
  }
}

/** Add `n` days (may be negative) to an ISO date; returns an ISO date. */
export function addDays(iso: string, n: number): string {
  return formatIsoUtc(parseIsoUtc(iso) + n * MS_PER_DAY);
}
/** Backend-named alias of {@link addDays}. */
export const addDaysIso = addDays;

/** Whole-day difference `a − b` (positive when `a` is later). */
export function diffDays(aIso: string, bIso: string): number {
  return Math.round((parseIsoUtc(aIso) - parseIsoUtc(bIso)) / MS_PER_DAY);
}

/** Today's calendar date in UTC, as `yyyy-mm-dd`. */
export function todayIsoUtc(): string {
  return formatIsoUtc(Date.now());
}
